<template>
  <v-card v-if="visible" outlined class="query-clarification pa-4 mb-4">
    <div class="d-flex align-start mb-3">
      <v-icon color="#F59E0B" class="mr-2 mt-0" size="20"
        >mdi-lightbulb-outline</v-icon
      >
      <div class="flex-grow-1">
        <div class="text-subtitle-2 font-weight-bold theme-heading">
          Help us understand your search better
        </div>
        <div class="text-body-2 mt-1 theme-subtext" style="opacity: 0.8">
          {{ subtitle }}
        </div>
      </div>
      <v-btn icon x-small @click="$emit('dismiss')">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>

    <div class="clarification-options">
      <div
        v-for="option in options"
        :key="option.query || option.label"
        class="clarification-option-card"
        @click="selectOption(option)"
      >
        <div class="option-label font-weight-bold text-body-2">
          {{ option.label }}
        </div>
        <div
          v-if="option.description"
          class="option-description text-caption"
          style="opacity: 0.7"
        >
          {{ option.description }}
        </div>
      </div>

      <div
        class="clarification-other-card"
        :class="{ 'other-active': showOtherInput }"
      >
        <div
          v-if="!showOtherInput"
          class="d-flex align-center"
          @click="showOtherInput = true"
          style="cursor: pointer"
        >
          <div>
            <div class="option-label font-weight-bold text-body-2">
              Other (specify)
            </div>
            <div class="option-description text-caption" style="opacity: 0.7">
              Describe what you're looking for in your own words
            </div>
          </div>
        </div>
        <div v-else>
          <div class="option-label font-weight-bold text-body-2 mb-2">
            Other (specify)
          </div>
          <div class="d-flex align-center">
            <v-text-field
              v-model="otherText"
              dense
              outlined
              hide-details
              placeholder="e.g. Someone with cloud migration experience in healthcare"
              class="flex-grow-1 mr-2"
              @keyup.enter="submitOther"
              autofocus
            />
            <v-btn
              small
              color="#007AFF"
              dark
              :disabled="!otherText.trim()"
              @click="submitOther"
            >
              Search
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-3">
      <a class="skip-link text-caption" @click="skipClarification">
        {{ skipLabel }}
      </a>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "QueryClarification",
  props: {
    clarification: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showOtherInput: false,
      otherText: "",
    };
  },
  computed: {
    visible() {
      return this.clarification && this.options.length > 0;
    },
    subtitle() {
      if (!this.clarification) return "";
      return (
        this.clarification.question ||
        this.clarification.message ||
        this.clarification.clarificationMessage ||
        "Your query could match several areas. Pick the most relevant focus:"
      );
    },
    options() {
      if (!this.clarification) return [];
      const raw =
        this.clarification.options ||
        this.clarification.suggestions ||
        this.clarification.refinements ||
        [];
      return raw.map((opt) => {
        if (typeof opt === "string")
          return { label: opt, query: opt, description: "" };
        return {
          label: opt.label || opt.text || opt.query,
          query: opt.constrainedQuery || opt.query || opt.text || opt.label,
          description: opt.description || "",
        };
      });
    },
    skipLabel() {
      if (!this.clarification) return "Search anyway without clarification";
      return (
        this.clarification.skipLabel || "Search anyway without clarification"
      );
    },
  },
  watch: {
    clarification() {
      this.showOtherInput = false;
      this.otherText = "";
    },
  },
  methods: {
    selectOption(option) {
      this.$emit("select", option.query || option.label || option);
    },
    submitOther() {
      const text = this.otherText.trim();
      if (text) {
        const original = this.clarification?.originalQuery || "";
        const combined = original ? original + " " + text : text;
        this.$emit("select", combined);
      }
    },
    skipClarification() {
      const original = this.clarification?.originalQuery || "";
      if (original) {
        this.$emit("skip", original);
      } else {
        this.$emit("dismiss");
      }
    },
  },
};
</script>

<style scoped>
.query-clarification {
  border-color: rgba(245, 158, 11, 0.4) !important;
}

.theme--light .query-clarification {
  background: #fffbeb;
}
.theme--dark .query-clarification {
  background: rgba(245, 158, 11, 0.08);
}

.clarification-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clarification-option-card {
  border: 1px solid rgba(0, 122, 255, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.theme--light .clarification-option-card {
  background: rgba(0, 122, 255, 0.04);
}
.theme--dark .clarification-option-card {
  background: rgba(0, 122, 255, 0.08);
}

.clarification-option-card:hover {
  border-color: #007aff;
  transform: translateX(4px);
}
.theme--light .clarification-option-card:hover {
  background: rgba(0, 122, 255, 0.08);
}
.theme--dark .clarification-option-card:hover {
  background: rgba(0, 122, 255, 0.15);
}

.clarification-other-card {
  border: 1px dashed rgba(128, 128, 128, 0.4);
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.2s ease;
}
.clarification-other-card:not(.other-active) {
  cursor: pointer;
}
.clarification-other-card:not(.other-active):hover {
  border-color: rgba(128, 128, 128, 0.7);
}
.other-active {
  border-color: #007aff;
  border-style: solid;
}

.skip-link {
  color: #007aff;
  cursor: pointer;
  text-decoration: underline;
  opacity: 0.8;
}
.skip-link:hover {
  opacity: 1;
}
</style>
